import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, TextField, Button, Stack } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

export const CrearJornadaManualAdmin = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [centros, setCentros] = useState([]);

  const [usuarioId, setUsuarioId] = useState("");
  const [centroId, setCentroId] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [comentario, setComentario] = useState("");

  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  // =========================
  // Carga inicial (usuarios + centros)
  // =========================
  useEffect(() => {
    cargarCombos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarCombos = async () => {
    setLoadingData(true);
    try {
      const [resUsuarios, resCentros] = await Promise.all([
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (resUsuarios.ok) setUsuarios(await resUsuarios.json());
      else toast.error("No se pudieron cargar los usuarios.");

      if (resCentros.ok) setCentros(await resCentros.json());
      else toast.error("No se pudieron cargar los centros.");
    } catch (err) {
      console.error("Error al cargar combos:", err);
      toast.error("Error de conexión al cargar usuarios/centros.");
    } finally {
      setLoadingData(false);
    }
  };

  // =========================
  // Opciones ordenadas (Autocomplete)
  // =========================
  const usuariosOrdenados = useMemo(() => {
    return (usuarios || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.localeCompare(`${b.nombre} ${b.apellidos || ""}`)
      );
  }, [usuarios]);

  const centrosOrdenados = useMemo(() => {
    return (centros || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const usuarioSeleccionado =
    usuariosOrdenados.find((u) => String(u.id) === String(usuarioId)) || null;

  const centroSeleccionado =
    centrosOrdenados.find((c) => String(c.id) === String(centroId)) || null;

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!usuarioId || !centroId || !inicio || !fin) {
      toast.warning("Usuario, centro, inicio y fin son obligatorios.");
      return;
    }

    const dIni = new Date(inicio);
    const dFin = new Date(fin);
    if (Number.isNaN(dIni.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dIni) {
      toast.warning("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        usuario_id: parseInt(usuarioId, 10),
        centro_id: parseInt(centroId, 10),
        inicio,
        fin,
        comentario,
      };

      const res = await fetch(`${Global.url}jornadas/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Jornada creada correctamente.");

        // Limpieza
        setUsuarioId("");
        setCentroId("");
        setInicio("");
        setFin("");
        setComentario("");
        return;
      }

      const errorData = await res.json().catch(() => ({}));
      toast.error(errorData?.message || "No se pudo crear la jornada.");
    } catch (error) {
      console.error("Error al crear jornada:", error);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSaving(false);
    }
  };

  const limpiarFormulario = () => {
    setUsuarioId("");
    setCentroId("");
    setInicio("");
    setFin("");
    setComentario("");
    toast.info("Formulario limpiado.");
  };

  // =========================
  // Render
  // =========================
  return (
      <Box className="appContainer">
        <Stack spacing={2}>
          {/* ✅ Cabecera homogénea (igual que listados con DataGrid) */}
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Inserción Manual de Jornada
          </Typography>

          {/* ✅ Wrapper que garantiza centrado del bloque */}
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Paper
              sx={{
                p: 2,
                width: "100%",
                maxWidth: 900,
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  alignItems: "start",
                }}
              >
                {/* Usuario */}
                <Autocomplete
                  size="small"
                  options={usuariosOrdenados}
                  getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                  value={usuarioSeleccionado}
                  onChange={(_, newValue) => setUsuarioId(newValue ? newValue.id : "")}
                  loading={loadingData}
                  renderInput={(params) => (
                    <TextField {...params} label="Usuario" placeholder="Escribe para buscar…" required />
                  )}
                  clearOnEscape
                />

                {/* Centro */}
                <Autocomplete
                  size="small"
                  options={centrosOrdenados}
                  getOptionLabel={(c) => c.nombre || ""}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                  value={centroSeleccionado}
                  onChange={(_, newValue) => setCentroId(newValue ? newValue.id : "")}
                  loading={loadingData}
                  renderInput={(params) => (
                    <TextField {...params} label="Centro" placeholder="Escribe para buscar…" required />
                  )}
                  clearOnEscape
                />

                {/* Inicio */}
                <TextField
                  label="Inicio"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={inicio}
                  onChange={(e) => setInicio(e.target.value)}
                  required
                />

                {/* Fin */}
                <TextField
                  label="Fin"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={fin}
                  onChange={(e) => setFin(e.target.value)}
                  required
                />

                {/* Comentario */}
                <TextField
                  label="Motivo o comentario"
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Explica por qué se crea esta jornada manualmente"
                  multiline
                  minRows={3}
                  sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
                />

                {/* Acciones */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    gridColumn: "1 / -1",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    pt: 1,
                  }}
                >
                  <Button variant="outlined" onClick={limpiarFormulario} disabled={saving}>
                    Limpiar
                  </Button>

                  <Button type="submit" disabled={saving}>
                    {saving ? "Creando..." : "Crear jornada"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Stack>
      </Box>
    );


};
